import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'reactstrap';

import api from "../../../api";
import { getMonedas } from "../../../api/apicalls.js";
import { Monedas } from '../../../views/Monedas/Monedas/constants.js'
import ModalMonedas from '../../../views/Monedas/Monedas/Modal.js'
import { formatCurrency } from '../../../functions/functions';
import ParadigmaAsyncSeeker from "../../../components/ParadigmaAsyncSeeker/ParadigmaAsyncSeeker.js"
import ParadigmaCurrencyInput from "../../../components/ParadigmaCurrencyInput/ParadigmaCurrencyInput";
import ParadigmaLabeledInput from "../../../components/ParadigmaLabeledInput/ParadigmaLabeledInput.js"
import './PagarCuotaMonedas.scss';

/**
 * PagarCuotaMonedas
 *
 * Al pagar una cuota puede que se quiera pagar con otras monedas, no solo
 * la especificada para esa cuota, por ejemplo, una cuota en Dolares se abona 
 * en Pesos Argentinos (segun cotizacion), tambien es posible pagar con
 * un combinacion de esas dos monedas.
 *
 * Solo se aceptan dos monedas, una son los pesos argentinos y la
 * otra una moneda extranjera. Si la moneda de la cuota es extranjera, solo
 * se puede pagar con esa moneda y/o pesos argentinos.
 * 
 */
class PagarCuotaMonedas extends Component {
    constructor(props) {
        super(props);
        this.state = {
            errors: [],
            monedas: [],
            monedasSeleccionadas: [],
            montos_pagados: [],

            // (Nota) Todos los montos y la cotizacion se guardan como number.
            // El formateo a la denominacion argentina se hace unicamente al
            // pasarle el valor a los input.
            montoNativa: 0,
            montoExtranjera: 0,
            montoEquivalente: 0,
            cotizacionExtranjera: 0, // En caso de que el usuario quiera otra cotizacion distinta de la default
        }
    }

    static propTypes = {
        montoAPagar: PropTypes.number.isRequired,
        monedaCuota: PropTypes.object.isRequired,
        updateMontosPagados: PropTypes.func, // Retorna los montos pagados al componente padre
    }

    componentDidMount() {
        this.loadMonedas()
    }

    componentDidUpdate(prevProps) {
        if (prevProps.monedaCuota !== this.props.monedaCuota) {
            this.initCotizacionExtranjera()
        }
    }

    loadMonedas = async () => {
        // Se guardan las monedas de la api
        const monedas = await getMonedas();

        // (Nota) La cotizacion se inicializa recien aca y no en el
        // componentDidMount, porque hace falta tener las monedas de la api.
        // Tampoco alcanza con el componentDidUpdate: el componente se monta con
        // la moneda de la cuota ya cargada (el padre lo renderiza recien cuando
        // la tiene), asi que nunca llega a ver un cambio de moneda.
        this.setState(
            { monedas: monedas ? monedas : [] },
            this.initCotizacionExtranjera
        )
    }

    /**
    * Se setea la cotizacion de la moneda extranjera solo en el caso de
    * que la moneda de la cuota sea extranjera.
    * De lo contrario, si la moneda de la cuota es pesos, hay que esperar
    * a que el usuario seleccione una moneda extranjera para iniciar
    * la cotizacion.
    */
    initCotizacionExtranjera = () => {
        const { monedaCuota } = this.props
        const { monedas } = this.state

        if (!monedaCuota || (monedaCuota.id === Monedas.PESOS)) {
            return
        }

        // (Nota) Se prefiere la cotizacion de la api porque es la que el usuario
        // puede editar desde el modal de monedas. La que viene adentro de la
        // cuota no tiene la cotizacion en pesos.
        const monedaExtranjera = monedas.find(moneda => moneda.id === monedaCuota.id);
        if (monedaExtranjera) {
            this.setState({
                cotizacionExtranjera: monedaExtranjera.cotizacion_pesos
            })
        }
    }

    resetState = () => {
        // (Nota) No se resetea el arreglo de monedas asi no hay que recuperarlo
        // denuevo de la api.
        this.setState({
            errors: [],
            monedasSeleccionadas: [],
            montos_pagados: [],

            montoNativa: 0,
            montoExtranjera: 0,
            montoEquivalente: 0,
            cotizacionExtranjera: 0, // En caso de que el usuario quiera otra cotizacion distinta de la default
        })
    }

    /**
    * Pasa a number lo que devuelven los ParadigmaCurrencyInput, que es el
    * rawValue de Cleave: un string con punto decimal (ej "100.5" si el usuario
    * escribio "100,5").
    *
    * (Nota) Es el unico lugar donde entra un valor que no es number. Todo lo
    * que llega de la api ya viene como number.
    */
    parseMonto = (valorInput) => {
        const numero = parseFloat(valorInput)

        return isNaN(numero) ? 0 : numero
    }

    onChangeField(field, value) {
        this.setState(prevState => {
            let errors = prevState.errors;
            if (errors) errors[field] = null;
            prevState.errors = errors;
            prevState[field] = value;
            return prevState;
        });
    }

    getError(field) {
        let errors = this.state.errors;
        if (errors) return errors[field];
            else return null;
    }

    /**
    * Le retorna los montos pagados al componente padre, formateados para
    * ser usados en la api.
    */
    returnFormattedMontosPagados = () => {
        const { monedaCuota, updateMontosPagados } = this.props
        const {
            monedasSeleccionadas,
            montoNativa,
            montoExtranjera,
            cotizacionExtranjera,
        } = this.state

        const monedaNativa = this.getMonedaNativa()
        const monedaExtranjera = this.getMonedaExtranjera()
        let cotizacionObjetivo = 0

        if (!monedaNativa || !monedaCuota || !updateMontosPagados) {
            return
        }

        if (this.esMonedaNativa(monedaCuota)){
            cotizacionObjetivo = monedaNativa.cotizacion_pesos;
        }

        if (this.esMonedaExtranjera(monedaCuota)){
            cotizacionObjetivo = cotizacionExtranjera;
        }

        // (Nota) Se filtran los nulos porque una moneda seleccionada que no sea
        // ni la nativa ni la extranjera no genera ningun monto pagado.
        const formattedMontosPagados = monedasSeleccionadas.map(moneda => {
            if (this.esMonedaNativa(moneda)){
                return {
                    fuente_monto: montoNativa,
                    moneda_fuente_id: monedaNativa.id,
                    fuente_cotizacion_pesos: monedaNativa.cotizacion_pesos,
                    moneda_objetivo_id: monedaCuota.id,
                    objetivo_cotizacion_pesos: cotizacionObjetivo,
                };
            }
            if (this.esMonedaExtranjera(moneda)){
                return {
                    fuente_monto: montoExtranjera,
                    moneda_fuente_id: monedaExtranjera.id,
                    fuente_cotizacion_pesos: cotizacionExtranjera,
                    moneda_objetivo_id: monedaCuota.id,
                    objetivo_cotizacion_pesos: cotizacionObjetivo,
                };
            }

            return null
        }).filter(Boolean)

        updateMontosPagados(formattedMontosPagados)
    }

    getMonedaNativa = () => {
        const { monedas } = this.state

        const monedaPesos = monedas.find(moneda => moneda.id === Monedas.PESOS);

        return monedaPesos ? monedaPesos : null
    }

    getMonedaExtranjera = () => {
        const { monedaCuota } = this.props
        const { monedasSeleccionadas } = this.state
        const monedaNativa = this.getMonedaNativa()

        if (!monedaNativa || !monedaCuota) {
            return null
        }

        if (monedaCuota.id !== monedaNativa.id){
            return monedaCuota
        }

        // (Nota) Puede no haber moneda extranjera todavia, eso pasa cuando la
        // cuota esta en pesos y el usuario aun no eligio una moneda extranjera.
        return monedasSeleccionadas.find(moneda => moneda.id !== monedaNativa.id) || null
    }

    /**
    * Comparaciones de monedas a prueba de nulos.
    *
    * Retornan false si alguna de las dos monedas todavia no existe, asi no hay
    * que repetir el chequeo en cada uno de los lugares donde se comparan.
    */
    esMonedaNativa = (moneda) => {
        const monedaNativa = this.getMonedaNativa()

        return Boolean(moneda && monedaNativa && (moneda.id === monedaNativa.id))
    }

    esMonedaExtranjera = (moneda) => {
        const monedaExtranjera = this.getMonedaExtranjera()

        return Boolean(moneda && monedaExtranjera && (moneda.id === monedaExtranjera.id))
    }

    getMontoTotal = () => {
        const { monedaCuota } = this.props;
        const { 
            montoNativa,
            montoExtranjera, 
            montoEquivalente, 
            monedasSeleccionadas,
        } = this.state;

        let montoTotal = 0;

        monedasSeleccionadas.forEach(moneda => {
            if (this.esMonedaNativa(moneda)){
                if (this.esMonedaNativa(monedaCuota)){
                    montoTotal += montoNativa;
                }
                if (this.esMonedaExtranjera(monedaCuota)){
                    montoTotal += montoEquivalente;
                }
            }

            if (this.esMonedaExtranjera(moneda)){
                if (this.esMonedaNativa(monedaCuota)){
                    montoTotal +=  montoEquivalente;
                }
                if (this.esMonedaExtranjera(monedaCuota)){
                    montoTotal += montoExtranjera;
                }
            }
        });

        return montoTotal
    }

    /**
    * Solo se pueden seleccionar 2 monedas máximo.
    * Si hay 2 monedas seleccionas, una si o si tiene que ser la moneda nativa.
    *
    * Si la cuota esta en moneda nativa, se puede seleccionar la moneda nativa
    * y/o cualquier otra moneda extranjera. Ej, cuota en Pesos se puede pagar 
    * con Dolares, Euros o Libras.
    *
    * Si la cuota esta en moneda extranjera, solo se puede seleccionar la moneda
    * nativa y/o esa misma moneda extranjera de la cuota y no otra. Ej, cuota 
    * en Dolares solo se puede pagar en Dolares y/o Pesos.
    *
    * retorna false si no es valido.
    * retorna true si es valido.
    */
    validateMonedasSeleccionada = (monedasSeleccionadas) => {
        const { monedaCuota } = this.props;
        const { errors } = this.state;
        const monedaNativa = this.getMonedaNativa();

        if (!monedaNativa || !monedaCuota) {
            return false
        }

        const monedaExtranjeraSeleccionada = monedasSeleccionadas.find(
            moneda => !this.esMonedaNativa(moneda)
        );
        const monedaNativaSeleccionada = monedasSeleccionadas.find(
            moneda => this.esMonedaNativa(moneda)
        );
        const newErrors = { ...errors };
        delete newErrors.monedas;

        if (monedasSeleccionadas.length > 2){
            newErrors['monedas'] = [{code: "invalid", detail: "Solo se pueden seleccionar hasta 2 monedas."}];
            this.setState({ errors: newErrors });
            return false
        }

        if ((monedasSeleccionadas.length === 2) && !monedaNativaSeleccionada){
            newErrors['monedas'] = [{code: "invalid", detail: `Una de las 2 monedas tiene que ser ${monedaNativa.nombre}`}];
            this.setState({ errors: newErrors });
            return false
        }

        // Si la cuota esta en moneda extranjera, la unica moneda extranjera que
        // se puede elegir es la de la cuota.
        // Si la cuota esta en pesos no se valida nada, el usuario puede pagar
        // con cualquier moneda extranjera.
        //
        // (Nota) Se compara por id y no por objeto: la moneda de la cuota viene
        // del componente padre y la seleccionada del buscador, son instancias
        // distintas aunque sean la misma moneda.
        if (this.esMonedaExtranjera(monedaCuota) && monedaExtranjeraSeleccionada && (monedaExtranjeraSeleccionada.id !== monedaCuota.id)){
            newErrors['monedas'] = [{code: "invalid", detail: `La moneda extranjera tiene que ser ${monedaCuota.nombre}`}];
            this.setState({ errors: newErrors });
            return false
        }

        return true
    }

    /**
    * Se comparan los id de las monedas seleccionadas del estado anterior con las 
    * del nuevo estado para obtener la ultima moneda seleccionada.
    */
    getUltimaMonedaSeleccionada = (newMonedasSeleccionadas, monedasSeleccionadas) => {
        const diferenciaEntreListas = newMonedasSeleccionadas.filter(
            newMoneda =>
                !monedasSeleccionadas.some(
                    moneda => moneda.id === newMoneda.id
                )
        );
        const ultimaMonedaSeleccionada = diferenciaEntreListas.length === 1 ? diferenciaEntreListas[0] : null;

        return ultimaMonedaSeleccionada
    }

    convertirMontoNativaAExtranjera = (montoNativa, cotizacionExtranjera) => {
        // (Nota) Sin cotizacion no se puede convertir, se devuelve 0 en lugar
        // de dividir por cero.
        if (!cotizacionExtranjera) {
            return 0
        }

        return montoNativa / cotizacionExtranjera
    }

    convertirMontoExtranjeraANativa = (montoExtranjera, cotizacionExtranjera) => {
        return montoExtranjera * cotizacionExtranjera
    }

    /**
    * Retorna A - B
    */
    calcularMontoDiferencia = (montoA, montoB) => {
        return montoA - montoB
    }

    /**
    * Lo que falta del montoAPagar despues de descontar lo que ya cubre la otra
    * moneda.
    *
    * (Nota) No baja de cero: si la otra moneda ya cubre mas que el total, a
    * esta le toca 0 y no un monto negativo.
    */
    calcularMontoRestante = (montoAPagar, montoYaCubierto) => {
        const restante = this.calcularMontoDiferencia(montoAPagar, montoYaCubierto)

        return restante > 0 ? restante : 0
    }

    onChangeMoneda = (newMonedasSeleccionadas) => {
        const {
            montoAPagar,
            monedaCuota,
        } = this.props;
        const { monedasSeleccionadas } = this.state;

        const monedaNativa = this.getMonedaNativa()
        const ultimaMonedaSeleccionada = this.getUltimaMonedaSeleccionada(newMonedasSeleccionadas, monedasSeleccionadas)
        const isCuotaEnMonedaNativa = this.esMonedaNativa(monedaCuota)
        // (Nota) Se parte de los montos que ya estan cargados, no de cero, asi
        // lo que el usuario venia cargando no se pierde al agregar o quitar una
        // moneda. Solo se recalcula el monto de la moneda que cambio.
        let montoNativa = this.state.montoNativa;
        let montoExtranjera = this.state.montoExtranjera;
        let montoEquivalente = this.state.montoEquivalente;
        let cotizacionExtranjera = this.state.cotizacionExtranjera;

        if (!monedaNativa || !monedaCuota) {
            return
        }

        if (!this.validateMonedasSeleccionada(newMonedasSeleccionadas)){
            return
        }

        // Se guarda la cotizacion de la moneda extranjera default en caso
        // de que la moneda seleccionada sea extranjera.
        //
        // (Nota) Tiene que ir antes del autocompletado, sino las conversiones
        // de abajo usan la cotizacion vieja (en la primer seleccion todavia es
        // 0 y da todo cero).
        //
        // (Nota) Solo se resetea la cotizacion en caso de que la moneda de la
        // cuota sea nativa, eso es porque la moneda extranjera puede ser
        // cualquiera y tienen cotizaciones distintas.
        // De lo contrario, si la moneda de la cuota es extranjera, no se
        // resetea la cotizacion porque es la unica moneda extranjera que se
        // puede elegir.
        //
        // (Nota) Se usa "no es la nativa" en lugar de "es la extranjera" porque
        // la moneda extranjera se calcula del estado anterior, que todavia no
        // incluye la moneda que el usuario acaba de elegir.
        if (ultimaMonedaSeleccionada && isCuotaEnMonedaNativa && !this.esMonedaNativa(ultimaMonedaSeleccionada)){
            cotizacionExtranjera = ultimaMonedaSeleccionada.cotizacion_pesos
        }

        if (newMonedasSeleccionadas.length === 0){
            montoNativa = 0;
            montoExtranjera = 0;
            montoEquivalente = 0;
        }

        // Con una sola moneda, esa moneda cubre el total.
        //
        // (Nota) Aplica tanto cuando se elige la primera moneda como cuando se
        // quita una de las dos y esta queda sola, por eso se mira la moneda que
        // quedo seleccionada y no la ultima que se agrego (al quitar una no hay
        // ultima moneda seleccionada).
        if (newMonedasSeleccionadas.length === 1){
            const monedaSeleccionada = newMonedasSeleccionadas[0];

            if (this.esMonedaNativa(monedaSeleccionada)){
                montoExtranjera = 0;

                if (isCuotaEnMonedaNativa){
                    montoNativa = montoAPagar;
                    montoEquivalente = 0;
                }
                if (!isCuotaEnMonedaNativa){
                    montoEquivalente = montoAPagar;
                    montoNativa = this.convertirMontoExtranjeraANativa(montoAPagar, cotizacionExtranjera);
                }
            }

            if (!this.esMonedaNativa(monedaSeleccionada)){
                montoNativa = 0;

                if (isCuotaEnMonedaNativa){
                    montoEquivalente = montoAPagar;
                    montoExtranjera = this.convertirMontoNativaAExtranjera(montoAPagar, cotizacionExtranjera);
                }
                if (!isCuotaEnMonedaNativa){
                    montoExtranjera = montoAPagar;
                    montoEquivalente = 0;
                }
            }
        }

        // Con dos monedas, la que se acaba de agregar cubre lo que falta para
        // llegar al total y la otra conserva lo que el usuario ya habia cargado.
        if ((newMonedasSeleccionadas.length === 2) && ultimaMonedaSeleccionada){
            if (this.esMonedaNativa(ultimaMonedaSeleccionada)){
                // Lo que ya cubre la moneda extranjera, en la moneda de la cuota
                const yaCubierto = isCuotaEnMonedaNativa ? montoEquivalente : montoExtranjera;
                const restante = this.calcularMontoRestante(montoAPagar, yaCubierto);

                if (isCuotaEnMonedaNativa){
                    montoNativa = restante;
                }
                if (!isCuotaEnMonedaNativa){
                    montoEquivalente = restante;
                    montoNativa = this.convertirMontoExtranjeraANativa(restante, cotizacionExtranjera);
                }
            }

            if (!this.esMonedaNativa(ultimaMonedaSeleccionada)){
                // Lo que ya cubre la moneda nativa, en la moneda de la cuota
                const yaCubierto = isCuotaEnMonedaNativa ? montoNativa : montoEquivalente;
                const restante = this.calcularMontoRestante(montoAPagar, yaCubierto);

                if (isCuotaEnMonedaNativa){
                    montoEquivalente = restante;
                    montoExtranjera = this.convertirMontoNativaAExtranjera(restante, cotizacionExtranjera);
                }
                if (!isCuotaEnMonedaNativa){
                    montoExtranjera = restante;
                }
            }
        }

        this.setState({
            monedasSeleccionadas: newMonedasSeleccionadas,
            montoNativa,
            montoExtranjera,
            montoEquivalente,
            cotizacionExtranjera,
        }, () => {
                this.returnFormattedMontosPagados()
            })
    }

    /**
    * Al cambiar el monto equivalente hay que actualizar el monto de la moneda
    * nativa o extranjera, depende de la moneda de la cuota.
    *
    * Si hay 2 monedas seleccionadas hay que autocompletar el monto de la otra 
    * moneda. 
    */
    onChangeMontoEquivalente = (valorInput) => {
        const {
            monedaCuota,
            montoAPagar,
        } = this.props;
        const {
            monedasSeleccionadas,
            cotizacionExtranjera,
        } = this.state;

        const monedaNativa = this.getMonedaNativa()
        const montoEquivalente = this.parseMonto(valorInput);
        let montoNativa = 0;
        let montoExtranjera = 0;

        if (!monedaNativa || !monedaCuota) {
            return
        }

        if (monedasSeleccionadas.length === 1){
            if (this.esMonedaNativa(monedaCuota)){
                montoExtranjera = this.convertirMontoNativaAExtranjera(montoEquivalente, cotizacionExtranjera);
            }

            if (!this.esMonedaNativa(monedaCuota)){
                montoNativa = this.convertirMontoExtranjeraANativa(montoEquivalente, cotizacionExtranjera);
            }
        }

        if (monedasSeleccionadas.length === 2){
            if (this.esMonedaNativa(monedaCuota)){
                montoNativa = this.calcularMontoDiferencia(montoAPagar, montoEquivalente);
                montoExtranjera = this.convertirMontoNativaAExtranjera(montoEquivalente, cotizacionExtranjera);
            }

            if (!this.esMonedaNativa(monedaCuota)){
                montoExtranjera = this.calcularMontoDiferencia(montoAPagar, montoEquivalente);
                montoNativa = this.convertirMontoExtranjeraANativa(montoEquivalente, cotizacionExtranjera);
            }
        }

        this.setState({
            montoEquivalente,
            montoNativa,
            montoExtranjera,
        }, () => {
                this.returnFormattedMontosPagados()
            })
    }

    /**
    * Este input solo esta habilitado cuando la cuota esta en pesos, porque es
    * la unica forma de que el monto en pesos sea el monto de la moneda de la
    * cuota.
    *
    * Con una sola moneda seleccionada alcanza con guardar el monto.
    *
    * Si hay 2 monedas seleccionadas, lo que no se paga en pesos se paga con la
    * moneda extranjera, asi que hay que autocompletar el resto.
    */
    onChangeMontoNativa = (valorInput) => {
        const {
            monedaCuota,
            montoAPagar,
        } = this.props;
        const {
            monedasSeleccionadas,
            cotizacionExtranjera,
        } = this.state;

        const monedaNativa = this.getMonedaNativa()
        const montoNativa = this.parseMonto(valorInput);
        let montoEquivalente = this.state.montoEquivalente;
        let montoExtranjera = this.state.montoExtranjera;

        if (!monedaNativa || !monedaCuota) {
            return
        }

        if ((monedasSeleccionadas.length === 2) && this.esMonedaNativa(monedaCuota)){
            montoEquivalente = this.calcularMontoDiferencia(montoAPagar, montoNativa);
            montoExtranjera = this.convertirMontoNativaAExtranjera(montoEquivalente, cotizacionExtranjera);
        }

        this.setState({
            montoNativa,
            montoEquivalente,
            montoExtranjera,
        }, () => {
                this.returnFormattedMontosPagados()
            })
    }

    /**
    * Este input solo esta habilitado cuando la cuota esta en la moneda
    * extranjera, es el caso espejo de onChangeMontoNativa.
    *
    * Si hay 2 monedas seleccionadas, lo que no se paga con la moneda extranjera
    * se paga en pesos.
    */
    onChangeMontoExtranjera = (valorInput) => {
        const {
            monedaCuota,
            montoAPagar,
        } = this.props;
        const {
            monedasSeleccionadas,
            cotizacionExtranjera,
        } = this.state;

        const monedaNativa = this.getMonedaNativa()
        const montoExtranjera = this.parseMonto(valorInput);
        let montoEquivalente = this.state.montoEquivalente;
        let montoNativa = this.state.montoNativa;

        if (!monedaNativa || !monedaCuota) {
            return
        }

        if ((monedasSeleccionadas.length === 2) && !this.esMonedaNativa(monedaCuota)){
            montoEquivalente = this.calcularMontoDiferencia(montoAPagar, montoExtranjera);
            montoNativa = this.convertirMontoExtranjeraANativa(montoEquivalente, cotizacionExtranjera);
        }

        this.setState({
            montoExtranjera,
            montoEquivalente,
            montoNativa,
        }, () => {
                this.returnFormattedMontosPagados()
            })
    }

    /**
    * Al cambiar la cotizacion solo se recalcula el monto de la moneda que no es
    * la de la cuota, que es el unico que sale de una conversion.
    *
    * El monto en la moneda de la cuota y la equivalencia los cargo el usuario y
    * estan expresados en la moneda de la cuota, asi que la cotizacion no los
    * afecta.
    */
    onChangeCotizacionExtranjera = (valorInput) => {
        const { monedaCuota } = this.props;
        const {
            montoEquivalente,
        } = this.state;

        const monedaNativa = this.getMonedaNativa()
        const cotizacionExtranjera = this.parseMonto(valorInput);
        // (Nota) Se parte de los montos actuales, no de cero, asi no se pierde
        // el monto que el usuario ya habia cargado en la moneda de la cuota.
        let montoNativa = this.state.montoNativa;
        let montoExtranjera = this.state.montoExtranjera;

        if (!monedaNativa || !monedaCuota) {
            return
        }

        if (this.esMonedaNativa(monedaCuota)){
            montoExtranjera = this.convertirMontoNativaAExtranjera(montoEquivalente, cotizacionExtranjera);
        }

        if (!this.esMonedaNativa(monedaCuota)){
            montoNativa = this.convertirMontoExtranjeraANativa(montoEquivalente, cotizacionExtranjera);
        }

        this.setState({
            cotizacionExtranjera,
            montoNativa,
            montoExtranjera,
        }, () => {
                this.returnFormattedMontosPagados()
            })
    }

    render() {
        const { monedaCuota } = this.props;
        const {
            monedasSeleccionadas,

            montoNativa,
            montoExtranjera,
            montoEquivalente,
            cotizacionExtranjera,
        } = this.state;
        const fieldClassNames = ["ui-field-label-col", "ui-field-control-col"];

        const monedaNativa = this.getMonedaNativa()

        // Se muestra el componente solo cuando este cargado todo lo necesario para funcionar
        if (!monedaNativa || !monedaCuota) {
            return ("Cargando...")
        }

        // (Nota) Puede ser null, la cuota puede estar en pesos y el usuario
        // todavia no haber elegido una moneda extranjera.
        const monedaExtranjera = this.getMonedaExtranjera()

        // Si la moneda seleccionada es la misma que la de la cuota, el monto se
        // carga directo y alcanza con un solo input.
        // Si es distinta, se muestra el monto en esa moneda (deshabilitado) y al
        // lado la equivalencia en la moneda de la cuota, que es la que el usuario
        // edita. Asi el usuario siempre carga montos en la moneda de la cuota.
        const esMonedaDeLaCuota = (monedaSeleccionada) =>
            monedaSeleccionada.id === monedaCuota.id

        return (
            <Fragment>
                <ParadigmaLabeledInput
                    md={[12, 12]}
                    classNames={fieldClassNames}
                    label={"Monedas"}
                    inputComponent={
                        <ParadigmaAsyncSeeker
                            url={api.monedas.monedas}
                            parameters={{
                                paginationEnabled:false,
                            }}
                            value={monedasSeleccionadas.map(moneda => moneda.id)}
                            multiselect={true}
                            onChange={(monedas) => this.onChangeMoneda(monedas)}
                        />
                    }
                    error={() => this.getError('monedas')}
                />

                {/* Cotización de la Moneda Extranjera */}
                {monedasSeleccionadas.map(monedaSeleccionada => (
                    <Fragment key={monedaSeleccionada.id}>
                        {!esMonedaDeLaCuota(monedaSeleccionada) && (
                            <ParadigmaLabeledInput
                                md={[12, 12]}
                                classNames={fieldClassNames}
                                label={`Cotización ${monedaExtranjera.nombre}`}
                                inputComponent={
                                    <div className="ui-inline-control">
                                        <ParadigmaCurrencyInput
                                            disabled={false}
                                            className="form-control monto w-100"
                                            symbol={monedaNativa.simbolo}
                                            value={formatCurrency(cotizacionExtranjera)}
                                            onChange={this.onChangeCotizacionExtranjera}
                                        />

                                        <div className="edit-button">
                                            <ModalMonedas
                                                id={() => monedaExtranjera.id}
                                                action="EDIT_COTIZACION"
                                                onSubmit={data => this.onChangeCotizacionExtranjera(data.cotizacion_pesos)}
                                            />
                                        </div>
                                    </div>
                                }
                            />
                        )}
                    </Fragment>
                ))}


                {/* Moneda Extranjera, la cuota esta en esa misma moneda */}
                {monedasSeleccionadas.map(monedaSeleccionada => (
                    <Fragment key={monedaSeleccionada.id}>
                        {this.esMonedaExtranjera(monedaSeleccionada) && esMonedaDeLaCuota(monedaSeleccionada) &&
                            <Row>
                                <Col className="col-6"/>

                                <Col className="col-6">
                                    <ParadigmaLabeledInput
                                        md={[12, 12]}
                                        classNames={fieldClassNames}
                                        label={monedaExtranjera.nombre}
                                        inputComponent={
                                            <ParadigmaCurrencyInput
                                                disabled={false}
                                                className="form-control monto w-100"
                                                symbol={monedaExtranjera.simbolo}
                                                value={formatCurrency(montoExtranjera)}
                                                onChange={this.onChangeMontoExtranjera}
                                            />
                                        }
                                    />
                                </Col>
                            </Row>
                        }
                    </Fragment>
                ))}


                {/* Moneda Nativa, se paga en pesos una cuota en otra moneda */}
                {monedasSeleccionadas.map(monedaSeleccionada => (
                    <Fragment key={monedaSeleccionada.id}>
                        {this.esMonedaNativa(monedaSeleccionada) && !esMonedaDeLaCuota(monedaSeleccionada) &&
                            <Row>
                                <Col className="col-6">
                                    <ParadigmaLabeledInput
                                        md={[12, 12]}
                                        classNames={fieldClassNames}
                                        label={monedaNativa.nombre}
                                        inputComponent={
                                            <ParadigmaCurrencyInput
                                                disabled={true}
                                                className="form-control monto w-100"
                                                symbol={monedaNativa.simbolo}
                                                value={formatCurrency(montoNativa)}
                                                onChange={() => {}} // Hacer nada
                                            />
                                        }
                                    />
                                </Col>

                                <Col className="col-6">
                                    <ParadigmaLabeledInput
                                        md={[12, 12]}
                                        classNames={fieldClassNames}
                                        label={"Equivalencia"}
                                        inputComponent={
                                            <ParadigmaCurrencyInput
                                                disabled={false}
                                                className="form-control monto w-100"
                                                symbol={monedaCuota.simbolo}
                                                value={formatCurrency(montoEquivalente)}
                                                onChange={this.onChangeMontoEquivalente}
                                            />
                                        }
                                    />
                                </Col>
                            </Row>
                        }
                    </Fragment>
                ))}


                {/* Moneda Nativa, la cuota tambien esta en pesos */}
                {monedasSeleccionadas.map(monedaSeleccionada => (
                    <Fragment key={monedaSeleccionada.id}>
                        {this.esMonedaNativa(monedaSeleccionada) && esMonedaDeLaCuota(monedaSeleccionada) &&
                            <Row>
                                <Col className="col-6"/>

                                <Col className="col-6">
                                    <ParadigmaLabeledInput
                                        md={[12, 12]}
                                        classNames={fieldClassNames}
                                        label={monedaNativa.nombre}
                                        inputComponent={
                                            <ParadigmaCurrencyInput
                                                disabled={false}
                                                className="form-control monto w-100"
                                                symbol={monedaNativa.simbolo}
                                                value={formatCurrency(montoNativa)}
                                                onChange={this.onChangeMontoNativa}
                                            />
                                        }
                                    />
                                </Col>
                            </Row>
                        }
                    </Fragment>
                ))}


                {/* Moneda Extranjera, se paga una cuota en pesos con otra moneda */}
                {monedasSeleccionadas.map(monedaSeleccionada => (
                    <Fragment key={monedaSeleccionada.id}>
                        {this.esMonedaExtranjera(monedaSeleccionada) && !esMonedaDeLaCuota(monedaSeleccionada) &&
                            <Row>
                                <Col className="col-6">
                                    <ParadigmaLabeledInput
                                        md={[12, 12]}
                                        classNames={fieldClassNames}
                                        label={monedaExtranjera.nombre}
                                        inputComponent={
                                            <ParadigmaCurrencyInput
                                                disabled={true}
                                                className="form-control monto w-100"
                                                symbol={monedaExtranjera.simbolo}
                                                value={formatCurrency(montoExtranjera)}
                                                onChange={() => {}} // Hacer nada
                                            />
                                        }
                                    />
                                </Col>

                                <Col className="col-6">
                                    <ParadigmaLabeledInput
                                        md={[12, 12]}
                                        classNames={fieldClassNames}
                                        label={"Equivalencia"}
                                        inputComponent={
                                            <ParadigmaCurrencyInput
                                                disabled={false}
                                                className="form-control monto w-100"
                                                symbol={monedaCuota.simbolo}
                                                value={formatCurrency(montoEquivalente)}
                                                onChange={this.onChangeMontoEquivalente}
                                            />
                                        }
                                    />
                                </Col>
                            </Row>
                        }
                    </Fragment>
                ))}


                {/* Monto Total */}
                {monedasSeleccionadas.length > 1 &&
                    <Row>
                        <Col className="col-6"/>

                        <Col className="col-6">
                            <ParadigmaLabeledInput
                                md={[12, 12]}
                                classNames={fieldClassNames}
                                label={"Total"}
                                inputComponent={
                                    <ParadigmaCurrencyInput
                                        disabled={true}
                                        className="form-control monto w-100"
                                        symbol={monedaCuota.simbolo}
                                        value={formatCurrency(this.getMontoTotal())}
                                        onChange={() => {}} // Hacer Nada
                                    />
                                }
                            />
                        </Col>
                    </Row>
                }

            </Fragment>
        );
    }
}

export default PagarCuotaMonedas;



// WEBPACK FOOTER //
// ./src/components/Pagar/PagarCuotaMonedas/PagarCuotaMonedas.js